import {
	Dialog,
	DropOverActionList,
	FormattedText,
	IconButton,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import { actionComponentFactory } from "client/Components/ActionList/actionsRenderHelpers";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import { isNil, isNotNil } from "client/Misc/utils";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { DocumentItemViewModel, RecentDocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import {
	AnonymousLinkInformation,
	disableLink,
	DisableLinkModel,
	getShareFileInfo,
	LinkInformation,
	SharedLinkKind,
	SharedLinksInformation,
	SpecificPeopleLinkInformation
} from "client/Api/Documents/SharePoint/sharePointSharing";
import {
	ActionKind,
	reduceToAvailableActions,
	RegularAction
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Fragment,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { LinkAudienceKind, LinkPermission } from "./common";
import styles from "./ManageLinksDialog.module.less";
import { SharedLink } from "./SharedLink";
import { useViewSpecificPeopleDetailsDialog } from "./ViewSpecificPeopleDetailsDialog";
import { TimeZone } from "client/DateAndTime/timezones";
import { Guid } from "client_legacy/ndaw/guid";

type ManageLinksDialogProps = {
	document: RecentDocumentItemViewModel | DocumentItemViewModel;
	onBack: () => void;
	onClose: () => void;
};

const ManageLinksDialog = (props: ManageLinksDialogProps): ReactElement => {
	const {
		document,
		onBack,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [links, setLinks] = useState<readonly SharedDocumentLink[] | undefined>();

	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [detailsDialog, openDetailsDialog] = useViewSpecificPeopleDetailsDialog();

	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					setIsBusy(true);

					const shareInfo: SharedLinksInformation = await getShareFileInfo(
						convertToLocationModel(document.location),
						document.id,
						signal
					);

					const links: readonly SharedDocumentLink[] = convertToSharedDocumentLinks(shareInfo);
					setLinks(links);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			document.id,
			document.location,
			stableOnClose
		]
	);

	const detailsHandler = useCallback(
		async (link: SpecificPeopleSharedDocumentLink): Promise<void> => {
			await openDetailsDialog({
				permission: link.permission,
				url: link.url,
				members: link.members,
				fileName: document.name
			});
		},
		[document.name, openDetailsDialog]
	);

	const deleteHandler = useCallback(
		async (link: SharedDocumentLink): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToDeleteThisLink"),
				confirmText: localize("_Delete"),
				trackingId: "delete-shared-link-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			try {
				setIsBusy(true);

				const { id, location } = document;

				const model: DisableLinkModel = {
					location: convertToLocationModel(location),
					itemId: id,
					kind: link.kind,
					shareId: link.id
				};

				await disableLink(model, globalCancellation);

				setLinks(
					(links) => {
						if (isNil(links)) {
							return links;
						}
						return links.filter((x) => x !== link);
					}
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_FailedToDeleteThisLink"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			document,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const actions = useMemo(
		(): readonly LinkAction[] => {
			return listActions({
				details: detailsHandler,
				delete: deleteHandler
			});
		},
		[deleteHandler, detailsHandler]
	);

	return (
		<>
			<Dialog trackingId="sp-share-link-manage-dialog" hasBusyIndicator={isBusy}>
				<Dialog.Header
					title={(
						<FormattedText
							text={localize("_ManageLinksForName")}
							thingsToReplace={[
								{ what: "{{Name}}", filler: document.name }
							]}
						/>
					)}
					onClose={onClose}
				/>

				<Dialog.Body>
					{isNotNil(links) && (
						<>
							{links.length === 0
								? (
									<p>{localize("_ThereAreNoLinksForThisItem")}</p>
								)
								: (
									<ul>
										{links.map((x, i): ReactElement => (
											<li key={i}>
												<Link
													value={x}
													actions={actions}
													timezone={userTimezone}
												/>
											</li>
										))}
									</ul>
								)
							}
						</>
					)}

				</Dialog.Body>

				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="secondary"
							text={localize("_Back")}
							onClick={onBack}
							trackingId="back"
						/>

						<Toolbar.Button
							kind="secondary"
							text={localize("_Close")}
							onClick={onClose}
							trackingId="close"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>

			{confirmationDialog}
			{detailsDialog}
		</>
	);
};
ManageLinksDialog.displayName = "ManageLinksDialog";

type LinkProps = {
	value: SharedDocumentLink;
	actions: readonly LinkAction[];
	timezone: TimeZone;
};

const Link = (props: LinkProps): ReactElement => {
	const {
		value,
		actions,
		timezone
	} = props;

	const [areActionsOpen, setAreActionsOpen] = useState<boolean>(false);
	const moreButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const actionContext = useMemo(
		(): LinkActionContext => ({
			link: value
		}),
		[value]
	);

	const availableActions = useMemo(
		(): readonly LinkAction[] => {
			return reduceToAvailableActions(
				actions,
				actionContext
			);
		},
		[actionContext, actions]
	);

	return (
		<div className={styles.item}>
			<div className={styles.link}>
				<SharedLink
					audience={value.audience}
					permission={value.permission}
					url={value.url}
					expirationDate={
						value.audience === LinkAudienceKind.Anonymous
							? value.expirationDate
							: undefined
					}
					trackingId="link"
					timezone={timezone}
					buttonKind="secondary"
				/>
			</div>
			<IconButton
				icon="ellipsis"
				kind="secondary"
				trackingId="more"
				shape="round"
				isDisabled={availableActions.length === 0}
				onClick={() => setAreActionsOpen(true)}
				ref={moreButtonRef}
			/>

			{moreButtonRef.current && areActionsOpen && (
				<DropOverActionList
					target={moreButtonRef.current}
					targetAlignment="br"
					contentAlignment="tr"
					onClose={() => setAreActionsOpen(false)}
					menuTitle={localize("_Actions")}
				>
					{availableActions.map((x) => (
						<Fragment key={x.name}>
							{actionComponentFactory(x, actionContext)}
						</Fragment>
					))}
				</DropOverActionList>
			)}
		</div>
	);
};
Link.displayName = "Link";

type BaseSharedDocumentLink<TLinkAudienceKind extends LinkAudienceKind = LinkAudienceKind> = {
	kind: SharedLinkKind;
	id: Guid;
	url: string;
	canBeManaged: boolean;
	audience: TLinkAudienceKind;
	permission: LinkPermission;
};

type AnonymousSharedDocumentLink = BaseSharedDocumentLink<LinkAudienceKind.Anonymous> & {
	expirationDate?: string | Date;
};

type SpecificPeopleSharedDocumentLink = BaseSharedDocumentLink<LinkAudienceKind.SpecificPeople> & {
	members: readonly string[];
};

type SharedDocumentLink = AnonymousSharedDocumentLink
	| SpecificPeopleSharedDocumentLink
	| BaseSharedDocumentLink<Exclude<LinkAudienceKind, LinkAudienceKind.Anonymous | LinkAudienceKind.SpecificPeople>>;


function convertToSharedDocumentLinks(info: SharedLinksInformation): readonly SharedDocumentLink[] {
	return [
		convertToAnonymousSharedDocumentLink(info.anonymousEdit, LinkPermission.CanEdit),
		convertToAnonymousSharedDocumentLink(info.anonymousView, LinkPermission.CanView),
		convertToSharedDocumentLink(info.organizationEdit, LinkAudienceKind.Organization, LinkPermission.CanEdit),
		convertToSharedDocumentLink(info.organizationView, LinkAudienceKind.Organization, LinkPermission.CanView),
		convertToSpecificPeopleShareDocumentLink(info.specificPeopleEdit, LinkPermission.CanEdit),
		convertToSpecificPeopleShareDocumentLink(info.specificPeopleView, LinkPermission.CanView)
	].filter(isNotNil);
}


function convertToAnonymousSharedDocumentLink(
	linkInfo: AnonymousLinkInformation,
	permission: LinkPermission
): AnonymousSharedDocumentLink | null {
	const baseLink: BaseSharedDocumentLink<LinkAudienceKind.Anonymous> | null
		= convertToSharedDocumentLink(linkInfo, LinkAudienceKind.Anonymous, permission);

	if (!baseLink) {
		return null;
	}

	return {
		...baseLink,
		expirationDate: linkInfo.expirationDate
	};
}

function convertToSpecificPeopleShareDocumentLink(
	linkInfo: SpecificPeopleLinkInformation,
	permission: LinkPermission
): SpecificPeopleSharedDocumentLink | null {
	const baseLink: BaseSharedDocumentLink<LinkAudienceKind.SpecificPeople> | null
		= convertToSharedDocumentLink(linkInfo, LinkAudienceKind.SpecificPeople, permission);

	if (!baseLink) {
		return null;
	}

	return {
		...baseLink,
		members: linkInfo.members
	};
}

function convertToSharedDocumentLink<TLinkAudienceKind extends LinkAudienceKind>(
	linkInfo: LinkInformation,
	audience: TLinkAudienceKind,
	permission: LinkPermission
): BaseSharedDocumentLink<TLinkAudienceKind> | null {
	if (!linkInfo || !linkInfo.isEnabled) {
		return null;
	}

	return {
		audience: audience,
		permission,
		url: linkInfo.url,
		canBeManaged: linkInfo.canBeChanged,
		kind: linkInfo.kind,
		id: linkInfo.id
	};
}

type LinkActionContext = {
	link: SharedDocumentLink;
};
type LinkAction = RegularAction<LinkActionContext>;

type LinkActionHandlers = {
	details: (link: SpecificPeopleSharedDocumentLink) => void;
	delete: (link: SharedDocumentLink) => void;
};

function listActions(handlers: LinkActionHandlers): readonly LinkAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "details",
			title: localize("_ViewDetails"),
			icon: "eye",
			canExecute: (c) => c.link.audience === LinkAudienceKind.SpecificPeople
				&& c.link.canBeManaged,
			execute: (c) => handlers.details(c.link as SpecificPeopleSharedDocumentLink)
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			icon: "trash-can",
			canExecute: (c) => c.link.canBeManaged,
			execute: (c) => handlers.delete(c.link)
		}
	];
}

export type ManageLinksDialogInitialData = Omit<ManageLinksDialogProps, "onClose" | "onBack">;

export function useManageLinksDialog(): [
	element: ReactElement | null,
	open: (props: ManageLinksDialogInitialData) => Promise<TryResult<{ goBack: boolean; }>>
] {
	return useDataInputDialog(
		(props, onClose, onDone) => (
			<ManageLinksDialog
				{...props}
				onClose={onClose}
				onBack={() => {
					onDone({
						goBack: true
					});
				}}
			/>
		)
	);
}
